/**
 * Email Thread Controller — signal-store-backed reading pane.
 *
 * Manages:
 *   - Clearing the loading spinner when the thread renders (connect)
 *   - Syncing list active-highlight via store.setCurrentThread
 *   - Expand/collapse messages (Turbo Frame src changes)
 *   - State mutations (archive/trash/star/read/etc.) → PATCH
 *   - Reply/forward/comment form lifecycle
 *   - Audit log toggle
 *   - TinyMCE sync before form submit
 *   - Mobile back navigation
 *   - Comment form show/hide (driven by store.commentFormVisible signal)
 */

import { Controller } from '@hotwired/stimulus'
import { patch, get } from '@rails/request.js'
import { I18n } from 'i18n-js'
import { effect } from '@preact/signals'
import locales from '../locales.json'
import { listContext } from '../modules/email/list-context.js'

export default class extends Controller {
  static targets = [
    'message', 'comment', 'replyForm', 'commentForm',
    'threadItems', 'scrollPane', 'auditEvents', 'auditToggle',
    'replyTrigger', 'replyFormExpanded', 'replyModeLabel', 'replyModeInput',
    'commentFormContainer'
  ]
  static values = { emailId: Number, mailboxId: Number }

  connect() {
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || 'hu'
    this._disposers = []

    // Clear loading spinner (set by email-list#navigateRow)
    const store = this._store()
    if (store) {
      store.threadLoaded()
      store.setCurrentThread(this.emailIdValue)
    }

    // Sync the list active-highlight with whichever thread is open
    this._syncListSelection(this.emailIdValue)

    // On mobile, eagerly load all lazy Turbo Frames
    if (window.innerWidth < 768) {
      this.element.querySelectorAll("turbo-frame[loading='lazy'][src]").forEach(frame => {
        frame.setAttribute('loading', 'eager')
      })
    }

    // ── Signal effects ──────────────────────────────────────────────────────

    if (store) {
      // commentFormVisible → show/hide comment form
      this._disposers.push(
        effect(() => {
          if (!this.hasCommentFormContainerTarget) return
          this.commentFormContainerTarget.classList.toggle(
            'd-none',
            !store.commentFormVisible.value
          )
          if (store.commentFormVisible.value) {
            // Scroll to bottom + focus trix-editor after paint
            requestAnimationFrame(() => requestAnimationFrame(() => {
              const pane = this.hasScrollPaneTarget ? this.scrollPaneTarget : this.element
              pane.scrollTo({ top: pane.scrollHeight, behavior: 'smooth' })
              this.commentFormContainerTarget.querySelector('trix-editor')?.focus()
            }))
          }
        })
      )
    }
  }

  disconnect() {
    this._disposers.forEach(d => d())
    this._disposers = []
  }

  // ── Sync list active highlight ────────────────────────────────────────────

  _syncListSelection(emailId) {
    const listEl = document.querySelector("[data-controller~='email-list']")
    if (!listEl) return
    const list = this.application.getControllerForElementAndIdentifier(listEl, 'email-list')
    list?.setActive(emailId)
  }

  // ── Message expand/collapse ───────────────────────────────────────────────

  async expandMessage(e) {
    const emailId = e.params.emailId
    const frame   = document.getElementById(`thread_msg_email_${emailId}`)
    if (frame) {
      frame.src = `/emails/threads/${emailId}/message/expanded`
      frame.reload()
    }
  }

  async collapseMessage(e) {
    const emailId = e.params.emailId
    const frame   = document.getElementById(`thread_msg_email_${emailId}`)
    if (frame) {
      frame.src = `/emails/threads/${emailId}/message/collapsed`
      frame.reload()
    }
  }

  // ── State actions ─────────────────────────────────────────────────────────

  async updateState(e) {
    e.preventDefault()
    const actionType = e.params.actionType
    await patch(`/emails/threads/${this.emailIdValue}/update_state`, {
      body: JSON.stringify({ action_type: actionType, ...listContext() }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  async archive(e) {
    e?.preventDefault?.()
    await patch(`/emails/threads/${this.emailIdValue}/update_state`, {
      body: JSON.stringify({ action_type: 'archive', ...listContext() }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  async toggleStar(e) {
    e?.preventDefault?.()
    const emailId = e?.params?.emailId || this.emailIdValue
    await patch(`/emails/threads/${emailId}/update_state`, {
      body: JSON.stringify({ action_type: 'star', ...listContext() }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  async changeStatus(e) {
    e.preventDefault()
    await patch(`/emails/threads/${this.emailIdValue}/change_status`, {
      body: JSON.stringify({ status: e.params.status }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  async assignResponsible(e) {
    e.preventDefault()
    await patch(`/emails/threads/${this.emailIdValue}/assign_responsible`, {
      body: JSON.stringify({ responsible_id: e.params.responsibleId }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  async moveToFolder(e) {
    e.preventDefault()
    const folderId = e.params.folderId || null
    await patch(`/emails/threads/${this.emailIdValue}/move_to_folder`, {
      body: JSON.stringify({ email_folder_id: folderId, ...listContext() }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })
  }

  // ── Reply form lifecycle ──────────────────────────────────────────────────

  startReply(e) {
    e?.preventDefault?.()
    this.fetchReplyForm('reply')
  }

  startReplyAll(e) {
    e?.preventDefault?.()
    this.fetchReplyForm('reply_all')
  }

  startForward(e) {
    e?.preventDefault?.()
    this.fetchReplyForm('forward')
  }

  async fetchReplyForm(mode) {
    await get(`/emails/compose/${mode}/${this.emailIdValue}`, { responseKind: 'turbo-stream' })
  }

  openReplyForm(e, mode = 'reply') {
    e?.preventDefault?.()
    e?.stopPropagation?.()

    const labels = {
      reply:     this.i18n.t('emails.reply'),
      reply_all: this.i18n.t('emails.reply_all'),
      forward:   this.i18n.t('emails.forward'),
    }
    if (this.hasReplyModeLabelTarget) {
      const labelEl = this.replyModeLabelTarget.querySelector('span') || this.replyModeLabelTarget
      labelEl.textContent = labels[mode] || labels.reply
    }
    if (this.hasReplyModeInputTarget)    this.replyModeInputTarget.value       = mode
    if (this.hasReplyTriggerTarget)      this.replyTriggerTarget.classList.add('d-none')
    if (this.hasReplyFormExpandedTarget) this.replyFormExpandedTarget.classList.remove('d-none')
  }

  closeReplyForm(e) {
    e?.preventDefault?.()
    if (this.hasReplyFormExpandedTarget) this.replyFormExpandedTarget.classList.add('d-none')
    if (this.hasReplyTriggerTarget)      this.replyTriggerTarget.classList.remove('d-none')
    window.tinymce?.remove('#email_quick_reply_editor')
  }

  syncTinymceBeforeSubmit() {
    window.tinymce?.get('email_quick_reply_editor')?.save()
  }

  // ── Audit log ─────────────────────────────────────────────────────────────

  async toggleAuditLog(e) {
    const store = this._store()
    if (e.currentTarget.checked) {
      if (store) store.toggleAuditLog(true)
      await get(`/emails/threads/${this.emailIdValue}/audit_events`, { responseKind: 'turbo-stream' })
    } else {
      if (store) store.toggleAuditLog(false)
      if (this.hasAuditEventsTarget) this.auditEventsTarget.innerHTML = ''
    }
  }

  // ── Comment form ──────────────────────────────────────────────────────────

  showCommentForm(e) {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    const store = this._store()
    if (store) {
      store.showCommentForm()
    } else {
      // Fallback without store
      if (this.hasCommentFormContainerTarget) {
        this.commentFormContainerTarget.classList.remove('d-none')
      }
    }
  }

  hideCommentForm() {
    const store = this._store()
    if (store) {
      store.hideCommentForm()
    } else {
      if (this.hasCommentFormContainerTarget) {
        this.commentFormContainerTarget.classList.add('d-none')
      }
    }
  }

  submitComment() {
    this.hideCommentForm()
  }

  // ── Recipient details toggle ──────────────────────────────────────────────

  toggleRecipientDetails(e) {
    e?.preventDefault?.()
    const emailId = e.params.emailId
    const details = document.getElementById(`recipient_details_${emailId}`)
    if (!details) return
    const icon = e.currentTarget.querySelector('.bi-chevron-down, .bi-chevron-up')
    details.classList.toggle('d-none')
    if (icon) {
      icon.classList.toggle('bi-chevron-down',  details.classList.contains('d-none'))
      icon.classList.toggle('bi-chevron-up',   !details.classList.contains('d-none'))
    }
  }

  // ── Mobile navigation ─────────────────────────────────────────────────────

  backToList() {
    const store = this._store()
    if (store && store.isMobileLayout.value) {
      store.showListPanel()
    } else {
      const layout = this.element.closest("[data-controller~='email-layout']")
      if (layout) {
        layout.classList.add('email-layout--show-list')
        layout.classList.remove('email-layout--show-pane')
      }
    }
  }

  mobileBack(e) {
    e?.preventDefault?.()
    this.backToList()
  }

  // ── Helper ────────────────────────────────────────────────────────────────

  _store() {
    return window._emailStore || null
  }
}
